import React, { useEffect, useRef, useState } from 'react';
import {Box, Chip, TextField, Typography} from '@mui/material';

const ChipDropper = ({ initialText, initialDescription, promptId, onSave, subCategory}) => {
  const [chips] = useState([
    // Profile - Blue
    { id: 1, displayName: 'Company Name', value: '{{companyName}}', category: 'profile', color: '#4299e1' },
    { id: 2, displayName: 'Company Industry', value: '{{companyIndustry}}', category: 'profile', color: '#4299e1' },
    { id: 3, displayName: 'Company Info', value: '{{companyInfo}}', category: 'profile', color: '#4299e1' },
    { id: 4, displayName: 'Company Solution', value: '{{companySolution}}', category: 'profile', color: '#4299e1' },
    { id: 5, displayName: 'Company Vertical', value: '{{companyVertical}}', category: 'profile', color: '#4299e1' },
    { id: 6, displayName: 'Company Value', value: '{{companyValue}}', category: 'profile', color: '#4299e1' },
    { id: 7, displayName: 'Persona User', value: '{{personaUser}}', category: 'profile', color: '#4299e1' },
    { id: 8, displayName: 'Persona Buyer', value: '{{personaBuyer}}', category: 'profile', color: '#4299e1' },
    { id: 9, displayName: 'Company Differentiator', value: '{{companyDifferentiator}}', category: 'profile', color: '#4299e1' },
    { id: 10, displayName: 'Customer Standards', value: '{{customerStandards}}', category: 'profile', color: '#4299e1' },
    // New Profile fields
    { id: 21, displayName: 'Languages', value: '{{languages}}', category: 'profile', color: '#4299e1' },
    { id: 22, displayName: 'ICP', value: '{{icp}}', category: 'profile', color: '#4299e1' },
    { id: 23, displayName: 'Company Type', value: '{{companyType}}', category: 'profile', color: '#4299e1' },
    { id: 24, displayName: 'Target Countries', value: '{{targetCountries}}', category: 'profile', color: '#4299e1' },

    // Customer & Competitors - Green
    { id: 11, displayName: 'Current Customers', value: '{{currentCustomers}}', category: 'customers', color: '#48bb78' },
    { id: 12, displayName: 'Potential Customers', value: '{{potentialCustomers}}', category: 'customers', color: '#48bb78' },
    { id: 13, displayName: 'Competitors', value: '{{competitors}}', category: 'customers', color: '#48bb78' },

    // Keywords - Purple
    { id: 14, displayName: 'Primary', value: '{{primary}}', category: 'keywords', color: '#9f7aea' },
    { id: 15, displayName: 'Secondary', value: '{{secondary}}', category: 'keywords', color: '#9f7aea' },
    { id: 16, displayName: 'Industry Keywords', value: '{{industryKeywords}}', category: 'keywords', color: '#9f7aea' },
    { id: 17, displayName: 'Product Keywords', value: '{{productKeywords}}', category: 'keywords', color: '#9f7aea' },
    // New Keywords fields
    { id: 25, displayName: 'SEO Keywords', value: '{{seoKeywords}}', category: 'keywords', color: '#9f7aea' },
    { id: 26, displayName: 'Forbidden Words', value: '{{forbiddenWords}}', category: 'keywords', color: '#9f7aea' },
    { id: 27, displayName: 'Internal Linking', value: '{{internalLinking}}', category: 'keywords', color: '#9f7aea' },

    // About - Orange
    { id: 18, displayName: 'Product Info', value: '{{productInfo}}', category: 'about', color: '#ed8936' },
    { id: 19, displayName: 'About Company', value: '{{aboutCompany}}', category: 'about', color: '#ed8936' },
    { id: 20, displayName: 'Author Information', value: '{{authorInformation}}', category: 'about', color: '#ed8936' },

    // Products - Pink/Magenta
    { id: 28, displayName: 'Custom Field 1', value: '{{customField1}}', category: 'products', color: '#e53e3e' },
    { id: 29, displayName: 'Custom Field 2', value: '{{customField2}}', category: 'products', color: '#e53e3e' },
    { id: 30, displayName: 'Custom Field 3', value: '{{customField3}}', category: 'products', color: '#e53e3e' },
    { id: 31, displayName: 'Product Offering 1', value: '{{productOffering1}}', category: 'products', color: '#e53e3e' },
    { id: 32, displayName: 'Product Offering 2', value: '{{productOffering2}}', category: 'products', color: '#e53e3e' },
    { id: 33, displayName: 'Product Offering 3', value: '{{productOffering3}}', category: 'products', color: '#e53e3e' },

    // Content Structure - Teal
    { id: 34, displayName: 'Title', value: '{{title}}', category: 'structure', color: '#319795' },
    { id: 35, displayName: 'Format', value: '{{format}}', category: 'structure', color: '#319795' },
    { id: 36, displayName: 'SubFormat', value: '{{subFormat}}', category: 'structure', color: '#319795' },
  ]);

  const [text, setText] = useState(initialText || '');
  const [description, setDescription] = useState(initialDescription || '');
  const [selectedChip, setSelectedChip] = useState(null); // Store currently selected chip
  const textareaRef = useRef(null);

  // Update text when prop changes
  useEffect(() => {
    if (initialText !== undefined) {
      setText(initialText);
    }
  }, [initialText, promptId]);

  // Update cursor style based on selected chip
  useEffect(() => {
    if (selectedChip) {
      document.body.style.cursor = 'copy';
    } else {
      document.body.style.cursor = 'default';
    }

    // Cleanup function to reset cursor when component unmounts
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [selectedChip]);

  /** Toggle chip selection */
  const handleChipClick = (chip) => {
    setSelectedChip((prev) => (prev?.id === chip.id ? null : chip));
  };

  /** Insert chip value into textarea at cursor position */
  const handleTextareaClick = (e) => {
    if (!selectedChip) return;

    const textarea = textareaRef.current;
    const cursorPos = textarea.selectionStart;

    const newText =
        text.substring(0, cursorPos) +
        selectedChip.value +
        text.substring(cursorPos);

    setText(newText);
    setSelectedChip(null); // clear chip selection after inserting
  };

  const handleTextChange = (e) => {
    setText(e.target.value);
  };

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
  };

  /** Helper to find chip by value for preview */
  const findChipByValue = (value) => chips.find((chip) => chip.value === value);

  /** Helper function to get chip styles based on selection state */
  const getChipStyles = (chip) => {
    const isSelected = selectedChip?.id === chip.id;

    return {
      cursor: 'pointer',
      backgroundColor: chip.color,
      color: "white",
      transform: isSelected ? 'scale(1.1)' : 'scale(1)',
      transition: 'all 0.2s ease-in-out',
      border: isSelected ? '3px solid #000' : '3px solid transparent',
      boxShadow: isSelected ? '0 0 15px rgba(0,0,0,0.5)' : '0 2px 4px rgba(0,0,0,0.1)',
      fontWeight: isSelected ? 'bold' : 'normal',
      '&:hover': {
        transform: 'scale(1.05)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
      }
    };
  };

  return (
      <Box
          sx={{ mx: 'auto', p: 3 }}
      >
        <Typography variant="h6" gutterBottom>
          Edit Prompt Content
        </Typography>

        {/* Instructions when chip is selected */}
        {selectedChip && (
            <Box sx={{
              mb: 2,
              p: 2,
              backgroundColor: '#e3f2fd',
              borderRadius: 1,
              border: '1px solid #2196f3'
            }}>
              <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                Selected: {selectedChip.displayName} - Click anywhere in the text area to insert it at that position
              </Typography>
            </Box>
        )}

        {/* Available Chips Grouped by Category */}
        <Box sx={{ mb: 3 }}>
          {/* Content Structure Category */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#319795' }}>
              Content Structure
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {chips
                  .filter(chip => chip.category === 'structure')
                  .map((chip) => (
                      <Chip
                          onClick={() => handleChipClick(chip)}
                          key={chip.id}
                          label={chip.displayName}
                          sx={getChipStyles(chip)}
                      />
                  ))
              }
            </Box>
          </Box>

          {/* Profile Category */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#4299e1' }}>
              Profile
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {chips
                  .filter(chip => chip.category === 'profile')
                  .map((chip) => (
                      <Chip
                          onClick={() => handleChipClick(chip)}
                          key={chip.id}
                          label={chip.displayName}
                          sx={getChipStyles(chip)}
                      />
                  ))
              }
            </Box>
          </Box>

          {/* Customer & Competitors Category */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#48bb78' }}>
              Customer & Competitors
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {chips
                  .filter(chip => chip.category === 'customers')
                  .map((chip) => (
                      <Chip
                          onClick={() => handleChipClick(chip)}
                          key={chip.id}
                          label={chip.displayName}
                          sx={getChipStyles(chip)}
                      />
                  ))
              }
            </Box>
          </Box>

          {/* Keywords Category */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#9f7aea' }}>
              Keywords
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {chips
                  .filter(chip => chip.category === 'keywords')
                  .map((chip) => (
                      <Chip
                          onClick={() => handleChipClick(chip)}
                          key={chip.id}
                          label={chip.displayName}
                          sx={getChipStyles(chip)}
                      />
                  ))
              }
            </Box>
          </Box>

          {/* About Category */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#ed8936' }}>
              About
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {chips
                  .filter(chip => chip.category === 'about')
                  .map((chip) => (
                      <Chip
                          onClick={() => handleChipClick(chip)}
                          key={chip.id}
                          label={chip.displayName}
                          sx={getChipStyles(chip)}
                      />
                  ))
              }
            </Box>
          </Box>

          {/* Products Category */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#e53e3e' }}>
              Products
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {chips
                  .filter(chip => chip.category === 'products')
                  .map((chip) => (
                      <Chip
                          onClick={() => handleChipClick(chip)}
                          key={chip.id}
                          label={chip.displayName}
                          sx={getChipStyles(chip)}
                      />
                  ))
              }
            </Box>
          </Box>
        </Box>

        {/* Description TextField */}
        {subCategory === "FORMAT" && <TextField
            autoFocus
            required
            fullWidth
            sx={{mb: 5}}
            margin="dense"
            id="description"
            name="description"
            label="Description"
            type="text"
            value={description}
            onChange={handleDescriptionChange}
        />}

        {/* Textarea */}
        <Box sx={{ position: 'relative', mb: 2 }}>
        <textarea
            ref={textareaRef}
            value={text}
            onClick={handleTextareaClick}
            onChange={handleTextChange}
            style={{
              width: '100%',
              minHeight: '300px',
              padding: '12px',
              fontFamily: 'monospace',
              fontSize: '14px',
              border: selectedChip ? '2px solid #2196f3' : '1px solid #ccc',
              borderRadius: '4px',
              resize: 'vertical',
              cursor: selectedChip ? 'copy' : 'text',
            }}
        />
        </Box>

        {/* Preview */}
        <Typography variant="body2" sx={{ color: 'gray', mb: 1 }}>
          Preview:
        </Typography>
        <Box
            sx={{
              bgcolor: 'grey.100',
              p: 2,
              borderRadius: 1,
              fontFamily: 'monospace',
              maxHeight: '200px',
              overflow: 'auto',
            }}
        >
          {text.split(/(\{\{[^}]+\}\})/g).map((part, index) => {
            if (part.match(/^\{\{[^}]+\}\}$/)) {
              const chipInfo = findChipByValue(part);
              return (
                  <Chip
                      key={index}
                      label={part}
                      sx={{
                        m: 0.5,
                        backgroundColor: chipInfo ? chipInfo.color : '#7F00FF',
                        color: 'white',
                      }}
                  />
              );
            } else {
              return <span key={index}>{part}</span>;
            }
          })}
        </Box>
      </Box>
  );
};

// Add this to expose the component's methods to parent
ChipDropper.displayName = 'ChipDropper';

export default ChipDropper;